import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Building2, ArrowLeft, Download, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function LegalDocView({ docId, testId }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [counsel, setCounsel] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/legal/${docId}`);
        setDoc(data);
      } catch (e) {
        setErr("Could not load document.");
      }
    })();
    // Best-effort counsel-status fetch; ignore failures (endpoint is authed
    // but returns 200 for signed-in users; anonymous will 401 silently).
    axios
      .get(`${API}/api/legal/counsel-status`, { withCredentials: true })
      .then((r) => setCounsel(r.data))
      .catch(() => setCounsel(null));
  }, [docId]);

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-200 py-16" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-3 text-white hover:opacity-80" data-testid="legal-home-link">
            <img src="/prestigious-logo.png" alt="Prestigious Property Management Services" className="h-11 w-11" />
          </Link>
          <Link to="/login" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5" data-testid="legal-back-login">
            <ArrowLeft className="h-3.5 w-3.5" /> Sign in
          </Link>
        </div>

        {err && <div className="text-red-300">{err}</div>}
        {!doc && !err && <div className="text-zinc-500">Loading…</div>}

        {doc && (
          <>
            <header className="mb-10">
              <div className="text-xs uppercase tracking-[0.28em] text-orange-400 mb-3">Legal</div>
              <h1 className="text-4xl sm:text-5xl font-semibold font-display text-white leading-tight">{doc.title}</h1>
              <div className="text-sm text-zinc-500 mt-3">
                Version {doc.version} · Effective {doc.effective_at} · Last updated {doc.updated_at}
              </div>
              <a
                href={`${API}/api/legal/${docId}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`legal-download-pdf-${docId}`}
                className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest rounded-full px-4 py-2 border border-white/15 bg-white/[0.03] text-zinc-200 hover:bg-white/[0.08] hover:text-white transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> Download counsel-ready PDF
              </a>
              {counsel?.signed && counsel.docs?.includes(docId) && (
                <div
                  className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[11px] uppercase tracking-widest"
                  data-testid={`legal-counsel-badge-${docId}`}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Counsel-signed
                  {counsel.last_event_at && (
                    <span className="text-emerald-400/70 normal-case tracking-normal text-[10px]">
                      · {new Date(counsel.last_event_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </header>
            <article className="space-y-8">
              {doc.sections?.map((s, i) => (
                <section key={i}>
                  <h2 className="text-lg font-semibold text-white mb-2 font-display">{s.title}</h2>
                  <p className="text-sm leading-relaxed text-zinc-400">{s.body}</p>
                </section>
              ))}
            </article>
            <footer className="mt-16 pt-8 border-t border-white/5 text-xs text-zinc-500 flex flex-wrap gap-4">
              <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/legal/downloads" className="hover:text-white" data-testid="footer-downloads">Contract downloads</Link>
              <span className="ml-auto">© 2026 Prestigious HOA Portal</span>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

export function TermsPage() {
  return <LegalDocView docId="terms" testId="terms-page" />;
}

export function PrivacyPage() {
  return <LegalDocView docId="privacy" testId="privacy-page" />;
}
